import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  AssignmentTurnedIn as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { superAdminAPI, SystemStats } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';

// Dark OLED Palette
const PALETTE = {
  bg: '#020617',
  surface: '#0F172A',
  surfaceAlt: '#1E293B',
  cta: '#22C55E',
  ctaDim: 'rgba(34, 197, 94, 0.15)',
  text: '#F8FAFC',
  textDim: '#94A3B8',
  border: 'rgba(248, 250, 252, 0.06)',
  borderHover: 'rgba(248, 250, 252, 0.12)',
  glow: (color: string) => `0 0 20px ${alpha(color, 0.25)}, 0 0 40px ${alpha(color, 0.1)}`,
};

const CHART_COLORS = ['#8B5CF6', '#22C55E', '#F59E0B', '#3B82F6'];

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await superAdminAPI.getSystemStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch superadmin stats', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: PALETTE.cta }} />
      </Box>
    );
  }

  if (!stats) return null;

  const roleData = [
    { name: 'سوپر ادمین', value: stats.users.superadmins },
    { name: 'ادمین‌ها', value: stats.users.admins },
    { name: 'مدیران', value: stats.users.managers },
    { name: 'پرسنل', value: stats.users.staff },
  ];

  const statCards = [
    {
      title: 'کل سازمان‌ها',
      value: stats.organizations.total,
      icon: <BusinessIcon sx={{ fontSize: 28 }} />,
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    },
    {
      title: 'کل کاربران سیستم',
      value: stats.users.total,
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      color: '#3B82F6',
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    },
    {
      title: 'رزروهای امروز (کل)',
      value: stats.reservations.today,
      icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
      color: '#22C55E',
      gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    },
    {
      title: 'مدیران ارشد',
      value: stats.users.superadmins,
      icon: <SecurityIcon sx={{ fontSize: 28 }} />,
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    },
  ];

  // Custom pie chart tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          background: PALETTE.surfaceAlt,
          border: `1px solid ${PALETTE.border}`,
          borderRadius: '12px',
          px: 2,
          py: 1.5,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.85rem', color: PALETTE.text, fontWeight: 600 }}>
            {payload[0].name}
          </Typography>
          <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.8rem', color: payload[0].payload.fill, fontWeight: 700 }}>
            {toPersianNumber(payload[0].value)} نفر
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom legend renderer
  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        {payload.map((entry: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color }} />
            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem', color: PALETTE.textDim }}>
              {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: '14px',
          background: `linear-gradient(135deg, ${PALETTE.cta} 0%, #16A34A 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: PALETTE.glow(PALETTE.cta),
        }}>
          <TrendingUpIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{
            fontFamily: 'Vazir, sans-serif',
            fontWeight: 800,
            color: PALETTE.text,
            letterSpacing: '-0.5px',
          }}>
            پیشخوان مدیریت سیستم
          </Typography>
          <Typography sx={{
            fontFamily: 'Vazir, sans-serif',
            fontSize: '0.85rem',
            color: PALETTE.textDim,
            mt: 0.25,
          }}>
            نمای کلی از وضعیت سامانه فودفلو
          </Typography>
        </Box>
      </Box>

      {/* Stat Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2.5,
          mb: 4,
        }}
      >
        {statCards.map((card, index) => (
          <Card
            key={index}
            sx={{
              background: PALETTE.surface,
              border: `1px solid ${PALETTE.border}`,
              borderRadius: '16px',
              color: PALETTE.text,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'default',
              '&:hover': {
                borderColor: alpha(card.color, 0.3),
                transform: 'translateY(-3px)',
                boxShadow: PALETTE.glow(card.color),
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: card.gradient,
                borderRadius: '16px 16px 0 0',
              },
            }}
          >
            <CardContent sx={{ p: '20px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontSize: '0.8rem',
                    color: PALETTE.textDim,
                    fontWeight: 500,
                    mb: 1.5,
                  }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 800,
                    fontSize: '2rem',
                    lineHeight: 1,
                    color: PALETTE.text,
                  }}>
                    {toPersianNumber(card.value)}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  background: alpha(card.color, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: card.color,
                }}>
                  {card.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Charts & Table Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1fr 1.2fr',
          },
          gap: 2.5,
        }}
      >
        {/* Pie Chart Card */}
        <Card sx={{
          background: PALETTE.surface,
          border: `1px solid ${PALETTE.border}`,
          borderRadius: '16px',
          color: PALETTE.text,
          transition: 'border-color 0.3s',
          '&:hover': { borderColor: PALETTE.borderHover },
        }}>
          <CardContent sx={{ p: '24px !important' }}>
            <Typography sx={{
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              mb: 1,
              color: PALETTE.text,
            }}>
              توزیع نقش‌ها
            </Typography>
            <Typography sx={{
              fontFamily: 'Vazir, sans-serif',
              fontSize: '0.78rem',
              color: PALETTE.textDim,
              mb: 2,
            }}>
              تعداد کاربران به تفکیک نقش
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={roleData}
                    cx="50%"
                    cy="45%"
                    innerRadius={65}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {roleData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        style={{ filter: `drop-shadow(0 0 6px ${alpha(CHART_COLORS[index % CHART_COLORS.length], 0.4)})` }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend content={renderLegend} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Recent Organizations Table */}
        <Card sx={{
          background: PALETTE.surface,
          border: `1px solid ${PALETTE.border}`,
          borderRadius: '16px',
          color: PALETTE.text,
          transition: 'border-color 0.3s',
          '&:hover': { borderColor: PALETTE.borderHover },
        }}>
          <CardContent sx={{ p: '24px !important' }}>
            <Typography sx={{
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              mb: 1,
              color: PALETTE.text,
            }}>
              آخرین سازمان‌های ثبت‌شده
            </Typography>
            <Typography sx={{
              fontFamily: 'Vazir, sans-serif',
              fontSize: '0.78rem',
              color: PALETTE.textDim,
              mb: 2.5,
            }}>
              جدیدترین سازمان‌های اضافه شده به سامانه
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['نام سازمان', 'تاریخ ثبت', 'وضعیت'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: PALETTE.textDim,
                          fontFamily: 'Vazir, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          borderBottom: `1px solid ${PALETTE.border}`,
                          pb: 1.5,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentOrganizations.map((org, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        transition: 'background 0.2s',
                        '&:hover': { background: alpha(PALETTE.text, 0.02) },
                      }}
                    >
                      <TableCell sx={{
                        color: PALETTE.text,
                        borderBottom: `1px solid ${PALETTE.border}`,
                        fontFamily: 'Vazir, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        py: 1.75,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '10px',
                            background: alpha(CHART_COLORS[index % CHART_COLORS.length], 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <BusinessIcon sx={{ fontSize: 16, color: CHART_COLORS[index % CHART_COLORS.length] }} />
                          </Box>
                          {org.name}
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        color: PALETTE.textDim,
                        borderBottom: `1px solid ${PALETTE.border}`,
                        fontFamily: 'Vazir, sans-serif',
                        fontSize: '0.82rem',
                        py: 1.75,
                      }}>
                        {new Date(org.created_at).toLocaleDateString('fa-IR')}
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: `1px solid ${PALETTE.border}`,
                        py: 1.75,
                      }}>
                        <Chip
                          label={org.is_active ? 'فعال' : 'غیرفعال'}
                          size="small"
                          sx={{
                            fontFamily: 'Vazir, sans-serif',
                            height: 24,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            borderRadius: '8px',
                            border: 'none',
                            background: org.is_active
                              ? alpha(PALETTE.cta, 0.12)
                              : alpha('#EF4444', 0.12),
                            color: org.is_active ? PALETTE.cta : '#EF4444',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;
